import React, { useEffect, useState } from 'react';

function parseBillTotal(order) {
  const n = Number(order?.totalAmount);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * Modal to enter amount collected; remainder becomes customer debt (backend).
 */
export default function SalesPaymentReceivedModal({
  open,
  order,
  onClose,
  onSubmit,
  submitting,
}) {
  const [amountStr, setAmountStr] = useState('');
  const [localError, setLocalError] = useState('');

  const bill = order ? parseBillTotal(order) : 0;

  useEffect(() => {
    if (!open || !order) return;
    setLocalError('');
    if (bill > 0) {
      setAmountStr(String(bill));
    } else {
      setAmountStr('0');
    }
  }, [open, order, bill]);

  if (!open || !order) return null;

  const collected = Math.round((parseFloat(String(amountStr).replace(',', '.')) || 0) * 100) / 100;
  const remainder = Math.max(0, Math.round((bill - collected) * 100) / 100);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');
    if (!Number.isFinite(collected) || collected < 0) {
      setLocalError('Enter a valid amount (0 or more).');
      return;
    }
    if (bill <= 0 && collected > 0) {
      setLocalError('This order has no bill total; collected amount must be 0.');
      return;
    }
    if (bill > 0 && collected > bill + 1e-9) {
      setLocalError('Amount collected cannot exceed the order total.');
      return;
    }
    try {
      await onSubmit(order._id, collected);
    } catch (err) {
      setLocalError(err.response?.data?.error || err.message || 'Request failed');
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full max-w-md p-4 sm:p-6"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="payment-modal-title"
      >
        <h2 id="payment-modal-title" className="text-lg font-bold text-gray-800 mb-1">
          Record payment
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Order <span className="font-mono font-semibold">{order.orderNo}</span>
        </p>

        {bill > 0 ? (
          <p className="text-sm text-gray-700 mb-3">
            Bill total: <span className="font-bold tabular-nums">SAR {bill.toFixed(2)}</span>
          </p>
        ) : (
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
            No bill total on this order. Collected amount will be recorded as 0.
          </p>
        )}

        <form onSubmit={handleSubmit}>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="amount-collected">
            Amount collected (SAR)
          </label>
          <input
            id="amount-collected"
            type="text"
            inputMode="decimal"
            value={amountStr}
            onChange={(e) => setAmountStr(e.target.value)}
            disabled={submitting}
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-gray-900 tabular-nums focus:ring-2 focus:ring-emerald-500 mb-2"
            autoComplete="off"
          />

          {bill > 0 && (
            <p className="text-sm text-gray-700 mb-3">
              Remaining (customer debt):{' '}
              <span className={`font-bold tabular-nums ${remainder > 0 ? 'text-amber-800' : 'text-emerald-700'}`}>
                SAR {remainder.toFixed(2)}
              </span>
            </p>
          )}

          {(localError || '').trim() && (
            <div className="mb-3 p-2 bg-red-50 border border-red-200 text-red-800 text-sm rounded-lg">{localError}</div>
          )}

          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end mt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2.5 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold disabled:opacity-50"
            >
              {submitting ? 'Saving…' : 'Confirm'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
